import { useState, useEffect } from "react";
import { Server, CheckCircle, XCircle, AlertCircle, Edit, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import API from "@/lib/api";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";

const STATUS_ICONS = { active: CheckCircle, inactive: XCircle, degraded: AlertCircle, failed: XCircle };
const STATUS_COLORS = { active: "text-emerald-600", inactive: "text-slate-500", degraded: "text-amber-600", failed: "text-red-600" };
const STATUS_BG = {
  active: "border-emerald-200 bg-emerald-50/50",
  inactive: "border-slate-200 bg-slate-50",
  degraded: "border-amber-200 bg-amber-50/50",
  failed: "border-red-200 bg-red-50/50",
};

export default function AdminProviders() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadProviders(); }, []);

  const loadProviders = async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/providers");
      setProviders(data.providers || []);
    } catch { toast.error("Failed to load providers"); }
    finally { setLoading(false); }
  };

  const openEdit = (p) => {
    setSelected(p);
    setForm({ name: p.name, status: p.status, cost_per_sms: p.cost_per_sms, priority: p.priority, email: "", api_key: "" });
    setEditModal(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        status: form.api_key ? "active" : form.status,
        cost_per_sms: parseInt(form.cost_per_sms),
        priority: parseInt(form.priority)
      };
      if (form.email || form.api_key) {
        payload.credentials = {};
        if (form.email) payload.credentials.email = form.email;
        if (form.api_key) payload.credentials.api_key = form.api_key;
      }
      await API.put(`/providers/${selected._id}`, payload);
      toast.success("Provider updated");
      setEditModal(false);
      loadProviders();
    } catch { toast.error("Failed to update provider"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid={ADMIN.providersPanel} className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">SMS Providers</h1>
          <p className="text-sm text-slate-500 mt-1">Configure and monitor SMS provider integrations</p>
        </div>
        <Button onClick={loadProviders} variant="outline" size="sm" className="border-slate-300 text-slate-600 h-9 hover:bg-slate-50">
          <RefreshCw size={14} className="mr-2" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-4">{[...Array(2)].map((_, i) => <div key={i} className="h-40 bg-slate-100 rounded-xl animate-pulse" />)}</div>
      ) : providers.length === 0 ? (
        <div className="text-center py-20 text-slate-400">No providers configured</div>
      ) : providers.map((p) => {
        const Icon = STATUS_ICONS[p.status] || Server;
        const hasCredentials = p.credentials?.email && p.credentials.email !== "***";
        return (
          <div key={p._id} className={`bg-white border rounded-xl p-6 shadow-sm ${STATUS_BG[p.status]}`}>
            <div className="flex items-start justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-[#eef1f8] border border-[#c0cfe8] rounded-xl flex items-center justify-center">
                  <Server size={18} className="text-[#0c1e41]" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">{p.name}</h3>
                  <span className="text-xs text-slate-500 capitalize">{p.provider_type}</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className={`text-xs flex items-center gap-1 border-current/20 ${STATUS_COLORS[p.status]}`}>
                  <Icon size={10} />
                  {p.status}
                </Badge>
                <button onClick={() => openEdit(p)} className="p-2 text-slate-500 hover:text-[#0c1e41] bg-slate-100 hover:bg-[#eef1f8] rounded-lg transition-colors">
                  <Edit size={14} />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: "Total Sent", value: p.total_sent?.toLocaleString() || "0" },
                { label: "Delivered", value: p.total_delivered?.toLocaleString() || "0" },
                { label: "Failed", value: p.total_failed?.toLocaleString() || "0" },
                { label: "Cost/SMS", value: `${p.cost_per_sms} credits` },
              ].map((s) => (
                <div key={s.label} className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
                  <div className="text-base font-bold font-mono text-slate-900">{s.value}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>

            <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-lg flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${(p.credentials?.api_key_configured || p.credentials?.api_key) ? "bg-emerald-500 pulse-green" : "bg-slate-400"}`} />
              <span className="text-xs text-slate-600">
                {(p.credentials?.api_key_configured || p.credentials?.api_key) 
                  ? `API key configured (${p.credentials?.api_key || "••••"}) — provider is active`
                  : "API key not configured — click Edit to add your BulkSMSLive API key"}
              </span>
            </div>

            {p.networks?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {p.networks.map((n) => <Badge key={n} variant="outline" className="text-xs bg-slate-50 border-slate-200 text-slate-600">{n}</Badge>)}
              </div>
            )}
          </div>
        );
      })}

      {/* Edit Modal */}
      <Dialog open={editModal} onOpenChange={setEditModal}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader><DialogTitle className="text-slate-900">Edit Provider — {selected?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700 text-sm">Provider Name</Label>
                <Input value={form.name || ""} onChange={(e) => setForm({...form,name:e.target.value})} className="bg-white border-slate-300 text-slate-900 h-10" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 text-sm">Status</Label>
                <select value={form.status || ""} onChange={(e) => setForm({...form,status:e.target.value})} className="w-full h-10 bg-white border border-slate-300 text-slate-900 rounded-md px-3 text-sm focus:outline-none focus:border-[#0c1e41]">
                  {["active","inactive","degraded"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 text-sm">Credits per SMS</Label>
                <Input type="number" value={form.cost_per_sms || ""} onChange={(e) => setForm({...form,cost_per_sms:e.target.value})} className="bg-white border-slate-300 text-slate-900 h-10" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 text-sm">Priority</Label>
                <Input type="number" value={form.priority || ""} onChange={(e) => setForm({...form,priority:e.target.value})} className="bg-white border-slate-300 text-slate-900 h-10" />
              </div>
            </div>
            <div className="border-t border-slate-200 pt-4">
              <p className="text-sm font-semibold text-[#0c1e41] mb-1">BulkSMSLive API Credentials</p>
              <p className="text-xs text-slate-500 mb-3">Leave blank to keep existing. Saving an API key activates the provider automatically.</p>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-slate-700 text-sm">Account Email <span className="text-slate-400 font-normal">(optional for reference)</span></Label>
                  <Input value={form.email || ""} onChange={(e) => setForm({...form,email:e.target.value})} placeholder="your-email@bulksmslive.com" className="bg-white border-slate-300 text-slate-900 h-10 placeholder:text-slate-400" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700 text-sm">API Key</Label>
                  <Input type="password" value={form.api_key || ""} onChange={(e) => setForm({...form,api_key:e.target.value})} placeholder="Paste your BulkSMSLive API key..." className="bg-white border-slate-300 text-slate-900 h-10 font-mono placeholder:text-slate-400" />
                  <p className="text-xs text-slate-400">Get your API key from <a href="https://www.bulksmslive.com" target="_blank" rel="noreferrer" className="text-[#0c1e41] hover:underline">bulksmslive.com</a> → Dashboard → Profile Summary</p>
                </div>
              </div>
            </div>
            <Button onClick={save} disabled={saving} className="w-full bg-[#0c1e41] hover:bg-[#091629]">
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
